/**
 *
 * @page 企业中心/数据看板/阶段满意度统计
 *
 **/

import dayjs from 'dayjs';
import { useReducer } from 'react';
import { useRouteMatch, useLocation } from 'react-router-dom';
import { useStoreState } from '&/utils/customHooks';
import { observer } from 'mobx-react';
import BasePageWrap from '&/components/ResourceLib/BasePageWrap';
import {
    Button,
    Form,
    Table,
    Spin,
    Space,
    Radio,
    message,
    Tooltip,
} from 'antd';
import { Select as AntSelect } from '&/components/AntdEncapsulation';
import {
    getManegers,
    getStageSatisfactionList,
    getStageSatisfactionDetail,
} from '@/api/personCenter/dataBoard';
import OrganizationSelect from '@/components/UserCenter/OrganizationSelect';
import BaseDrawer from '&/components/BaseDrawer';
import { DAY_OPTIONS, downloadExcel } from './constants';
import { getSelectedDayLabel } from './common';
import './StageSatisfactionList.scss';

function StageSatisfactionList() {
    const isAdmin = !useRouteMatch({
        path: '/user-center/manager/satisfaction-list',
        strict: false, // 全路径?
        sensitive: true, // 大小写?
    });

    const location = useLocation();
    const { search } = location;
    const urlParams = new URLSearchParams(search);
    const initDays = urlParams.get('days');

    const { entId } = useStoreState('dataBoard');

    const [result, setResult] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const dftParams = {
        days: initDays !== null ? +initDays : 30,
        isAll: 1,
        export: 0,
        div: 1,
    };

    if (isAdmin) dftParams.entId = entId;

    const [queryForm, setQueryForm] = useState(dftParams);

    const [didUpdate, forceUpdate] = useReducer(s => s + 1, 0);

    // 初始化调用和entId更新调用
    useEffect(() => {
        if (isAdmin) {
            setQueryForm({
                ...queryForm,
                entId,
                isAll: !entId ? 1 : 2,
            });

            setDetailParams(prev => ({
                ...prev,
                entId,
                isAll: !entId ? 1 : 2,
                managerId: null,
                export: 0,
            }));
        }
        forceUpdate();
    }, [entId]);

    useEffect(() => {
        queryManagerOptions(queryForm);
    }, [queryForm.entId]);

    const onParamChange = (val, key) => {
        setQueryForm(prev => ({ ...prev, [key]: val }));
        forceUpdate(); // 防止输入时不停触发搜索
    };

    const getCurTabName = () => {
        return optionsWithDisabled.find(item => item.value === queryForm.div)
            ?.label;
    };

    const getExcelFileName = () => {
        return (
            getCurTabName() +
            '-' +
            getSelectedDayLabel(DAY_OPTIONS(intl), queryForm.days) +
            '-' +
            dayjs().format('YYYY_MM_DD')
        );
    };

    const queryList = async () => {
        const PAYLOAD = { ...queryForm };
        !PAYLOAD.entId && delete PAYLOAD.entId;
        const { export: isExport } = PAYLOAD;
        try {
            !isExport && setIsLoading(true);
            const res = await getStageSatisfactionList(PAYLOAD);
            if (isExport) {
                downloadExcel(res, getExcelFileName()).catch(err => {
                    if (err.code === 0) {
                        message.warn('无内容');
                    } else {
                        message.warn(err?.msg);
                    }
                });
            } else {
                const { data } = res;
                const list = (data || []).map(item => {
                    item.stageName = item['_id'].stageName;
                    return item;
                });

                setResult(list);
            }
        } finally {
            setIsLoading(false);
            isExport && setQueryForm(prev => ({ ...prev, export: 0 }));
        }
    };

    useEffect(() => {
        didUpdate > 0 && queryList();
    }, [didUpdate]);

    const dftManeger = {
        value: null,
        label: intl.get('base.all'),
    };
    const [managerOpts, setManagerOpts] = useState([dftManeger]);

    function queryManagerOptions(payload) {
        const PARAMS = {
            isAll: payload.isAll,
            roleId: 2, // 项目经理
        };

        if (payload.entId) {
            PARAMS.entId = payload.entId;
        }
        getManegers(PARAMS).then(({ data }) => {
            const opts = [dftManeger];
            for (const el of data.list) {
                opts.push({
                    value: el.userId,
                    label: el.username,
                });
            }
            setManagerOpts(opts);
        });
    }

    const [visible, setVisible] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailData, setDetailData] = useState([]);
    const allProject = { value: null, label: '全部' };
    const [detailParams, setDetailParams] = useState({
        projectName: allProject,
        isAll: 1,
        managerId: null,
        export: 0,
        days: 30,
    });

    useEffect(() => {
        if (!visible) {
            initParams();
        }
    }, [visible]);

    useEffect(() => {
        visible && queryDetail();
    }, [detailParams]);

    const [detaileTitle, setDetaileTitle] = useState('');

    const [projectNameOpts, setProjectNameOpts] = useState([allProject]);

    function initParams() {
        setDetailParams(prev => ({
            ...prev,
            projectName: allProject,
            managerId: null,
            export: 0,
        }));
        setProjectNameOpts([]);
    }

    const showDetail = ({ stageName }) => {
        setVisible(true);
        setDetailParams(prev => ({
            ...prev,
            stageName,
            export: 0,
            days: queryForm.days,
        }));
        setDetaileTitle(`${stageName}—满意度统计`);
    };

    function queryDetail() {
        const PARAMS = { ...detailParams };
        if (detailParams.projectName?.value) {
            PARAMS.projectName = detailParams.projectName?.label;
        } else {
            PARAMS.projectName = '';
        }

        if (!detailParams.entId) delete PARAMS.entId;
        const { export: isExport } = PARAMS;
        !isExport && setDetailLoading(true);
        getStageSatisfactionDetail(PARAMS)
            .then(res => {
                if (isExport) {
                    downloadExcel(res, detaileTitle).catch(err => {
                        if (err.code === 0) {
                            message.warn('无内容');
                        } else {
                            message.warn(err?.msg);
                        }
                    });
                    return;
                }
                const { data } = res;
                setDetailData(data);
                // 根据projectId去重
                if (projectNameOpts.length) return;
                const result = Object.values(
                    data.reduce((acc, cur) => {
                        const { projectId, name } = cur;
                        acc[projectId] = {
                            value: projectId,
                            label: name,
                        };
                        return acc;
                    }, {})
                );
                setProjectNameOpts([allProject].concat(result));
            })
            .finally(() => {
                setDetailLoading(false);
            });
    }

    const onClose = () => {
        setVisible(false);
    };

    const { Item } = Form;

    const subColumns = [
        {
            title: intl.get('base.serialNum'),
            key: '1',
            width: 60,
            align: 'center',
            render: (_, __, idx) => idx + 1,
        },
        {
            title: '项目名称',
            dataIndex: 'name',
            align: 'center',
        },
        {
            title: '项目经理',
            dataIndex: 'managerName',
            align: 'center',
            ellipsis: true,
            render(val) {
                return (
                    <Tooltip title={val}>
                        <span>{val}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: '施工结果满意度',
            dataIndex: 'build',
            align: 'center',
            sorter: {
                compare: (a, b) => a.build - b.build,
                multiple: 1,
            },
        },
        {
            title: '项目经理满意度',
            dataIndex: 'manager',
            align: 'center',
            sorter: {
                compare: (a, b) => a.twe - b.twe,
                multiple: 1,
            },
        },
        {
            title: '业主意见反馈',
            dataIndex: 'remark',
            align: 'center',
            ellipsis: true,
            render(val) {
                return (
                    <Tooltip title={val}>
                        <span>{val}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: '提交时间',
            dataIndex: 'updateDate',
            align: 'center',
            render: date =>
                date ? dayjs.unix(date).format('YYYY.MM.DD HH:mm') : '',
        },
    ];

    const onExoprtDetail = () => {
        setDetailParams(prev => ({ ...prev, export: 1 }));
    };

    const detailModalEl = (
        <BaseDrawer
            visible={visible}
            title={
                <>
                    {detaileTitle}
                    <Button
                        type="primary"
                        onClick={onExoprtDetail}
                        disabled={!detailData.length}>
                        导出表单
                    </Button>
                </>
            }
            destroyOnClose
            bodyStyle={{
                padding: '1.375rem 1.25rem',
            }}
            className="inspection-item-detail"
            onClose={onClose}>
            <Spin spinning={detailLoading}>
                <Space>
                    <Item label={'项目名称'}>
                        <AntSelect
                            labelInValue
                            value={detailParams.projectName}
                            onChange={projectName =>
                                setDetailParams(prev => ({
                                    ...prev,
                                    projectName,
                                    export: 0,
                                }))
                            }
                            options={projectNameOpts}
                        />
                    </Item>

                    <Item label={'项目经理'}>
                        <AntSelect
                            onChange={e =>
                                setDetailParams(prev => ({
                                    ...prev,
                                    managerId: e,
                                    export: 0,
                                }))
                            }
                            options={managerOpts}
                            className="manager-select"
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? '')
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                            value={detailParams.managerId}
                        />
                    </Item>
                    <Item label={'时间区间'}>
                        <AntSelect
                            value={detailParams.days}
                            onChange={days =>
                                setDetailParams(prev => ({
                                    ...prev,
                                    days,
                                    export: 0,
                                }))
                            }
                            options={DAY_OPTIONS(intl)}
                        />
                    </Item>
                </Space>

                <Table
                    className="normal-table"
                    bordered
                    columns={subColumns}
                    dataSource={detailData}
                    size="small"
                    scroll={{
                        y: 'calc(100vh - 12.4rem)',
                    }}
                    pagination={false}
                />
            </Spin>
        </BaseDrawer>
    );

    const columns = [
        {
            title: intl.get('base.serialNum'),
            key: '1',
            width: 70,
            align: 'center',
            render: (_, __, idx) => idx + 1,
        },
        {
            title: '施工阶段',
            dataIndex: 'stageName',
            render: (stageName, row) => {
                return (
                    <span
                        className="native-link"
                        onClick={() => showDetail(row)}>
                        {stageName}
                    </span>
                );
            },
        },
        {
            title: '1星',
            dataIndex: 'one',
            sorter: {
                compare: (a, b) => a.one - b.one,
                multiple: 1,
            },
        },
        {
            title: '2星',
            dataIndex: 'twe',
            sorter: {
                compare: (a, b) => a.twe - b.twe,
                multiple: 1,
            },
        },
        {
            title: '3星',
            dataIndex: 'three',
            sorter: {
                compare: (a, b) => a.three - b.three,
                multiple: 1,
            },
        },
        {
            title: '4星',
            dataIndex: 'four',
            sorter: {
                compare: (a, b) => a.four - b.four,
                multiple: 1,
            },
        },
        {
            title: '5星',
            dataIndex: 'five',
            sorter: {
                compare: (a, b) => a.five - b.five,
                multiple: 1,
            },
        },
        {
            title: '更新时间',
            dataIndex: 'updateDate',
            align: 'center',
            render: date =>
                date ? dayjs.unix(date).format('YYYY.MM.DD HH:mm') : '',
        },
    ];

    const onExoprt = () => {
        setQueryForm(prev => ({ ...prev, export: 1 }));
        forceUpdate(); // 防止输入时不停触发搜索
    };

    const addBtn = (
        <Button type="primary" onClick={onExoprt} disabled={!result.length}>
            导出表单
        </Button>
    );

    const optionsWithDisabled = [
        {
            label: '施工结果满意度',
            value: 1,
        },
        {
            label: '项目经理满意度',
            value: 2,
        },
    ];

    return (
        <>
            {isAdmin && <OrganizationSelect />}
            <BasePageWrap
                title={<strong>阶段满意度统计</strong>}
                className="stage-fatisfaction-list"
                type="card"
                headerRight={addBtn}>
                <header className="member-list-form">
                    <Radio.Group
                        options={optionsWithDisabled}
                        onChange={e => onParamChange(e.target.value, 'div')}
                        value={queryForm.div}
                        optionType="button"
                        buttonStyle="solid"
                    />

                    <Item label={'时间区间'}>
                        <AntSelect
                            value={queryForm.days}
                            onChange={e => onParamChange(e, 'days')}
                            options={DAY_OPTIONS(intl)}
                        />
                    </Item>
                </header>

                <Table
                    rowKey="stageId"
                    className="normal-table"
                    loading={isLoading}
                    bordered
                    columns={columns}
                    dataSource={result}
                    size="small"
                    pagination={false} /* {
                        size: 'small',
                        current: queryForm.page,
                        onChange: handlePageChange,
                        defaultPageSize: queryForm.pageNum,
                        total: result.total,
                        showSizeChanger: false,
                        style: { marginBottom: 0 },
                        showTotal: total => intl.get('base.total', { total }),
                    } */
                />
            </BasePageWrap>
            {detailModalEl}
        </>
    );
}

export default observer(StageSatisfactionList);
